import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { Fragment } from "react";
import { useScrollLock } from "../../hooks";
import { ModalContent } from "./ModalContent";
import { ModalFooter } from "./ModalFooter";

const modalPanelStyles = cva("relative w-full transform bg-white text-left transition-all", {
	variants: {
		variant: {
			default: "mb-10 rounded-lg px-4 py-8 shadow-xl sm:my-8 sm:p-6",
			fullScreen:
				"h-dvh min-h-dvh overflow-y-auto px-4 py-6 sm:my-8 sm:mb-10 sm:h-auto sm:min-h-0 sm:rounded-lg sm:p-6 sm:shadow-xl",
		},
		size: {
			md: "sm:max-w-lg",
			lg: "max-w-3xl",
			xl: "max-w-7xl",
		},
	},
	defaultVariants: { variant: "default", size: "md" },
});

const modalLayoutStyles = cva("flex min-h-full justify-center text-center", {
	variants: {
		variant: {
			default: "items-end p-4 sm:items-center sm:p-0",
			fullScreen: "items-stretch p-0 sm:items-center sm:p-0",
		},
	},
	defaultVariants: { variant: "default" },
});

export type ModalVariant = NonNullable<VariantProps<typeof modalPanelStyles>["variant"]>;
export type ModalSize = NonNullable<VariantProps<typeof modalPanelStyles>["size"]>;

export type ModalProps = {
	isOpen: boolean;
	onClose?: () => void;
	onAnimationComplete?: () => void;
	children?: React.ReactNode;
	actions?: React.ReactNode;
	variant?: ModalVariant;
	size?: ModalSize;
	i18n: {
		closeButton: string;
		cancelButton: string;
	};
};

export const Modal = ({
	isOpen,
	onClose,
	onAnimationComplete,
	children,
	actions,
	variant = "default",
	size = "md",
	i18n,
}: ModalProps) => {
	useScrollLock({ enabled: isOpen });

	return (
		<Transition show={isOpen} as={Fragment} afterLeave={onAnimationComplete}>
			<Dialog
				as="div"
				className="relative z-50 font-sans print:hidden"
				onClose={() => (onClose ? onClose() : () => {})}
			>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-150"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-overlay transition-opacity" />
				</TransitionChild>

				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className={modalLayoutStyles({ variant })}>
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-150"
							enterFrom={
								variant === "fullScreen"
									? "opacity-0 sm:translate-y-0 sm:scale-95"
									: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							}
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo={
								variant === "fullScreen"
									? "opacity-0 sm:translate-y-0 sm:scale-95"
									: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							}
						>
							<DialogPanel className={modalPanelStyles({ variant, size })}>
								<ModalContent
									onClose={onClose}
									variant={variant}
									i18n={{ closeButton: i18n.closeButton }}
								>
									{children}
								</ModalContent>
								<ModalFooter
									actions={actions}
									onClose={onClose}
									variant={variant}
									i18n={{ cancelButton: i18n.cancelButton }}
								/>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};
